/**
 * M13 - seis abas e formularios de Configuracoes no shell existente.
 *
 * Os descritores compartilhados evitam repetir a mesma edicao para cada recurso.
 */
import { useEffect, useState, type FormEvent, type CSSProperties } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSessao } from '../../sessao.tsx';
import { useIdioma } from '../../i18n/index.tsx';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '../../layout/Shell.tsx';
import type { TelaDeModulo } from '../contrato.ts';
import { useTraducao, rotuloAtual, type ChaveConfiguracoes } from './i18n.ts';
import { aplicarMarca, tokensDaMarca, type Aparencia } from './aparencia.ts';
import './configuracoes.css';

type Dados = Record<string, unknown>;
type Recurso =
  | 'empresa' | 'aparencia' | 'sequencia' | 'campo' | 'visao' | 'modelo' | 'funcao' | 'meta_vendas';
type Campo = {
  chave: ChaveConfiguracoes;
  tipo?: 'numero' | 'booleano' | 'json' | 'linhas' | 'texto';
  opcoes?: readonly string[];
  padrao?: unknown;
  nulo?: boolean;
};
const campos: Record<Recurso, Campo[]> = {
  empresa: [
    { chave: 'razao_social' },
    { chave: 'nome_fantasia', nulo: true },
    { chave: 'documento', nulo: true },
    { chave: 'logo_ref', nulo: true },
    { chave: 'endereco', tipo: 'json', padrao: {} }
  ],
  aparencia: [
    { chave: 'acento', tipo: 'numero', padrao: 8 },
    { chave: 'tema', opcoes: ['escuro', 'claro'] },
    { chave: 'idioma', opcoes: ['pt', 'en', 'es'] }
  ],
  sequencia: [
    { chave: 'codigo', opcoes: ['pedido', 'fatura', 'contrato'] },
    { chave: 'empresa_id', nulo: true },
    { chave: 'prefixo' },
    { chave: 'padding', tipo: 'numero', padrao: 6 },
    { chave: 'reinicia_por_ano', tipo: 'booleano' }
  ],
  campo: [
    { chave: 'modulo', opcoes: ['nucleo', 'pessoas', 'leads', 'turmas', 'contratos', 'comercial'] },
    { chave: 'entidade', opcoes: ['empresas', 'pessoas', 'leads', 'turmas', 'contratos'] },
    { chave: 'chave' },
    { chave: 'rotulo' },
    { chave: 'tipo', opcoes: ['texto', 'numero', 'data', 'lista', 'booleano'] },
    { chave: 'opcoes', tipo: 'linhas', padrao: [] },
    { chave: 'obrigatorio', tipo: 'booleano' },
    { chave: 'ordem', tipo: 'numero', padrao: 0 }
  ],
  visao: [
    { chave: 'tela', padrao: 'pessoas' },
    { chave: 'nome' },
    { chave: 'filtros', tipo: 'json', padrao: {} },
    { chave: 'colunas', tipo: 'linhas', padrao: [] },
    { chave: 'ordenacao', tipo: 'json', padrao: [] },
    { chave: 'padrao', tipo: 'booleano' }
  ],
  modelo: [
    { chave: 'nome' },
    { chave: 'tipo', opcoes: ['mensagem', 'documento'] },
    { chave: 'assunto' },
    { chave: 'corpo', tipo: 'texto' }
  ],
  funcao: [{
      chave: 'nome'
    }, {
      chave: 'descricao',
      tipo: 'texto'
    }],
  meta_vendas: [
    { chave: 'mes' },
    { chave: 'valor', tipo: 'numero', padrao: 0 }
  ]
};
class ErroApi extends Error {
  readonly campos: string[];
  constructor(codigo: string, campos: string[]) {
    super(codigo);
    this.campos = campos;
  }
}

async function api<T>(acao: string, entrada: Dados = {}, escrita = false): Promise<T> {
  const qs = new URLSearchParams(
    Object.entries(entrada).map(
      ([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)]
    )
  );
  const r = await fetch(
    `/api/configuracoes/${acao}${escrita ? '' : `?${qs}`}`,
    {
      method: escrita ? 'POST' : 'GET',
      credentials: 'same-origin',
      ...(escrita
      ? {
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify(entrada)
      }
      : {})
    }
  );
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new ErroApi(
      String(d.code ?? 'rede'),
      Array.isArray(d.details)
      ? d.details.map((f: {
        campo?: string;
      }) => f.campo ?? '').filter(Boolean)
      : []
    );
  }
  return r.json() as Promise<T>;
}

function inicial(recurso: Recurso, item?: Dados): Dados {
  return Object.fromEntries(
    campos[recurso].map(
      c => {
        const v = item?.[c.chave] ?? c.padrao ?? (c.tipo === 'booleano' ? false : c.opcoes?.[0] ?? '');
        return [
          c.chave,
          c.tipo === 'json'
          ? JSON.stringify(v, null, 2)
          : c.tipo === 'linhas' ? (Array.isArray(v) ? v.join('\n') : '') : v
        ];
      }
    )
  );
}

function Historico({ recurso, id }: {
  recurso: Recurso;
  id?: string;
}) {
  const t = useTraducao();
  const q = useQuery({
    queryKey: ['configuracoes', 'historico', recurso, id],
    queryFn: () => api<{
      itens: Dados[];
    }>(
      'historico',
      id
      ? {
        chave: recurso === 'aparencia'
        ? 'configuracoes.aparencia'
        : `configuracoes.${recurso}.${id.replaceAll('-', '')}`
      }
      : {}
    )
  });
  return <details className='configuracoes__historico'>
    <summary>
      {t('historico')}
    </summary>
    {q.isPending
    ? <EstadoCarregando texto={t('carregando')} />
    : q.isError
    ? <EstadoErro titulo={t('historico')} texto={t('erro')} />
    : <>
      {!q.data.itens.length && <p>
        {t('semHistorico')}
      </p>}
      {q.data.itens.filter(d => String(d['chave']).startsWith(`configuracoes.${recurso}`)).map(
        d => <details key={String(d['id'])}>
          <summary>
            {String(d['criado_em'])}
          </summary>
          <p>
            {t('antes')}
          </p>
          <pre>
            {JSON.stringify(d['valor_antes'], null, 2)}
          </pre>
          <p>
            {t('depois')}
          </p>
          <pre>
            {JSON.stringify(d['valor_depois'], null, 2)}
          </pre>
        </details>
      )}
    </>}
  </details>;
}

function CatalogosExternos() {
  const t = useTraducao();
  const q = useQuery({
    queryKey: ['configuracoes', 'dependencias'],
    queryFn: () => api<{
      itens: Dados[];
    }>('dependencias')
  });
  return <section>
    <h2>
      {t('externos')}
    </h2>
    {q.isPending
    ? <EstadoCarregando texto={t('carregando')} />
    : q.isError
    ? <EstadoErro titulo={t('externos')} texto={t('erro')} />
    : q.data.itens.map(
      d => <div key={String(d['modulo'])} className='configuracoes__registro'>
        <h3>
          {t(d['modulo'] as 'pipeline' | 'financeiro')}
        </h3>
        {d['instalado']
        ? <a href={d['modulo'] === 'pipeline' ? '/comercial' : '/financeiro'}>
          {t('abrir')}
        </a>
        : <EstadoVazio
          titulo={t('naoInstalado')}
          texto={t(d['modulo'] as 'pipeline' | 'financeiro')} />}
      </div>
    )}
  </section>;
}

function usePreferenciasWorkspace() {
  const { trocar } = useIdioma();
  const q = useQuery({
    queryKey: ['configuracoes', 'aparencia'],
    queryFn: () => api<{
      itens: Dados[];
    }>('listar', {
        recurso: 'aparencia'
      })
  });
  useEffect(
    () => {
      const v = q.data?.itens[0] as Aparencia | undefined;
      if (!v) return;
      try {
        // Os padroes do workspace so entram quando o usuario ainda nao escolheu preferencias.
      if (!localStorage.getItem('erp_tema')) document.documentElement.dataset.tema = v.tema;
        if (!localStorage.getItem('erp_idioma')) trocar(v.idioma);
      } catch {
      }
      aplicarMarca(v);
    },
    [q.data, trocar]
  );
}

function Editor({ recurso }: {
  recurso: Recurso;
}) {
  usePreferenciasWorkspace();
  const t = useTraducao();
  const qc = useQueryClient();
  const { estado } = useSessao();
  const { trocar } = useIdioma();
  const [item, setItem] = useState<Dados | undefined>();
  const [form, setForm] = useState<Dados>(() => inicial(recurso));
  const [extras, setExtras] = useState<Dados>({});
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const [falhas, setFalhas] = useState<string[]>([]);
  const [sucesso, setSucesso] = useState(false);
  const [confirmar, setConfirmar] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['configuracoes', recurso],
    queryFn: () => api<{
      itens: Dados[];
    }>('listar', {
        recurso
      })
  });
  const empresas = useQuery({
    queryKey: ['configuracoes', 'empresa'],
    queryFn: () => api<{
      itens: Dados[];
    }>('listar', {
        recurso: 'empresa'
      }),
    enabled: recurso === 'sequencia'
  });
  const camposExtras = useQuery({
    queryKey: ['configuracoes', 'campo'],
    queryFn: () => api<{
      itens: Dados[];
    }>('listar', {
        recurso: 'campo'
      }),
    enabled: recurso === 'empresa'
  });
  useEffect(
    () => {
      if (recurso === 'aparencia' && q.data?.itens[0]) {
        const v = q.data.itens[0];
        setItem(v);
        setForm(inicial(recurso, v));
        aplicarMarca(v as Aparencia);
      }
    },
    [q.data, recurso]
  );
  const mudar = (chave: string, v: unknown) => setForm(f => ({
    ...f,
    [chave]: v
  }));
  const reset = () => {
    setItem(undefined);
    setForm(inicial(recurso));
    setExtras({});
    setErro('');
    setFalhas([]);
    setSucesso(false);
  };
  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setFalhas([]);
    setSucesso(false);
    const dados: Dados = {};
    try {
      for (const c of campos[recurso]) {
        const v = form[c.chave];
        dados[c.chave] = c.tipo === 'json'
        ? JSON.parse(String(v))
        : c.tipo === 'linhas'
        ? String(v).split('\n').map(v => v.trim()).filter(Boolean)
        : c.nulo && v === '' ? null : v;
      }
    } catch {
      setErro(t('jsonErro'));
      return;
    }
    if (recurso === 'visao' && estado.fase === 'dentro') {
      dados['usuario_id'] = estado.eu.usuario.id;
    }
    if (recurso === 'empresa') dados['extras'] = extras;
    setOcupado(true);
    try {
      const r = await api<{
        id: string;
      }>(
        `salvar_${recurso}`,
        {
          dados,
          ...(item ? {
            id: item['id']
          } : {})
        },
        true
      );
      setItem({
        id: r.id,
        ...dados
      });
      await qc.invalidateQueries({
        queryKey: ['configuracoes']
      });
      setSucesso(true);
      if (recurso === 'aparencia') {
        const v = dados as Aparencia;
        aplicarMarca(v);
        document.documentElement.dataset.tema = v.tema;
        trocar(v.idioma);
      }
    } catch (e) {
      setErro(t(e instanceof Error && e.message === 'duplicado' ? 'duplicado' : 'erro'));
      setFalhas(e instanceof ErroApi ? e.campos : []);
    } finally {
      setOcupado(false);
    }
  }

  async function arquivar(id: string) {
    setOcupado(true);
    setErro('');
    try {
      await api('excluir', {
        recurso,
        id
      }, true);
      reset();
      setConfirmar(null);
      await qc.invalidateQueries({
        queryKey: ['configuracoes']
      });
    } catch {
      setErro(t('erro'));
    } finally {
      setOcupado(false);
    }
  }
  const definicoes = (camposExtras.data?.itens ?? []).filter(
    c => c['entidade'] === 'empresas' && c['modulo'] === 'nucleo'
  );
  return <div className='configuracoes'>
    <h2>
      {t(recurso)}
    </h2>

    {q.isPending
    ? <EstadoCarregando texto={t('carregando')} />
    : q.isError
    ? <>
      <EstadoErro titulo={t(recurso)} texto={t('erro')} />
      <button className='botao' onClick={() => void q.refetch()}>
        {t('repetir')}
      </button>
    </>
    : <>

      {!q.data.itens.length && <EstadoVazio titulo={t('vazio')} texto={t('vazioTexto')} />}

      {recurso !== 'aparencia'
      && <div className='configuracoes__lista'>
        {q.data.itens.map(
          d => <article key={String(d['id'])} className='configuracoes__registro'>
            <div>
              <strong>
                {String(d['razao_social'] ?? d['rotulo'] ?? d['nome'] ?? d['codigo'])}
              </strong>
              {recurso === 'sequencia' && <p>{t('proximo')}: {String(d['proximo_numero'])}</p>}
              {recurso === 'empresa' && typeof d['logo_ref'] === 'string'
              && <img src={d['logo_ref']} alt={String(d['razao_social'])} width='64' height='64' />}
            </div>
            <div className='configuracoes__acoes'>
              <button
                className='botao'
                onClick={() => {
                  setItem(d);
                  setForm(inicial(recurso, d));
                  setExtras((d['extras'] ?? {}) as Dados);
                  setErro('');
                  setFalhas([]);
                  setSucesso(false);
                }}>
                {t('editar')}
              </button>
              {['campo', 'visao', 'modelo', 'funcao', 'meta_vendas'].includes(recurso)
              && <button
                className='botao'
                disabled={ocupado}
                onClick={() => setConfirmar(String(d['id']))}>
                {t('excluir')}
              </button>}
              {confirmar === d['id']
              && <>
                <button
                  className='botao'
                  disabled={ocupado}
                  onClick={() => void arquivar(String(d['id']))}>
                  {t('confirmar')}
                </button>
                <button className='botao' onClick={() => setConfirmar(null)}>
                  {t('cancelar')}
                </button>
              </>}
            </div>
          </article>
        )}
      </div>}

      <h3>
        {item ? t('editar') : t('novo')}
      </h3>

      {recurso === 'visao' && <p>
        {t('privada')}
      </p>}
      {recurso === 'campo' && item && <p>
        {t('identidade')}
      </p>}

      <form className='configuracoes__form' onSubmit={e => void salvar(e)}>
        {campos[recurso].map(
          c => {
            const imutavel = recurso === 'campo' && !!item
            && ['modulo', 'entidade', 'chave', 'tipo', 'opcoes'].includes(c.chave);
            return <label key={c.chave} className='configuracoes__campo'>
              {t(c.chave)}
              {c.chave === 'empresa_id'
              ? <select
                value={String(form[c.chave] ?? '')}
                onChange={e => mudar(c.chave, e.target.value)}>
                <option value=''>
                  {t('geral')}
                </option>
                {empresas.data?.itens.map(
                  v => <option key={String(v['id'])} value={String(v['id'])}>
                    {String(v['razao_social'])}
                  </option>
                )}
              </select>
              : c.opcoes
              ? <select
                disabled={imutavel || ocupado}
                value={String(form[c.chave])}
                onChange={e => mudar(c.chave, e.target.value)}>
                {c.opcoes.map(
                  v => <option key={v} value={v}>
                    {t(v === 'documento' ? 'documentoModelo' : v as ChaveConfiguracoes)}
                  </option>
                )}
              </select>
              : ['json', 'linhas', 'texto'].includes(c.tipo ?? '')
              ? <textarea
                disabled={imutavel || ocupado}
                value={String(form[c.chave])}
                onChange={e => mudar(c.chave, e.target.value)} />
              : c.tipo === 'booleano'
              ? <input
                type='checkbox'
                disabled={ocupado}
                checked={Boolean(form[c.chave])}
                onChange={e => mudar(c.chave, e.target.checked)} />
              : <input
                disabled={imutavel || ocupado}
                type={c.chave === 'acento' ? 'range' : c.tipo === 'numero' ? 'number' : 'text'}
                {...(c.chave === 'acento' ? {
                  min: 0,
                  max: 360
                } : {})}
                value={String(form[c.chave] ?? '')}
                onChange={e => mudar(
                  c.chave,
                  c.tipo === 'numero' ? Number(e.target.value) : e.target.value,
                )}
               />}
            </label>;
          }
        )}

        {recurso === 'empresa'
        && (camposExtras.isPending
        ? <EstadoCarregando texto={t('carregando')} />
        : camposExtras.isError
        ? <EstadoErro titulo={t('campo')} texto={t('erro')} />
        : definicoes.map(
          c => <label key={String(c['id'])} className='configuracoes__campo'>
            {String(c['rotulo'])}
            {c['tipo'] === 'lista'
            ? <select
              value={String(extras[String(c['chave'])] ?? '')}
              onChange={e => setExtras(v => ({
                ...v,
                [String(c['chave'])]: e.target.value
              }))}>
              <option value='' />
              {(c['opcoes'] as string[]).map(v => <option key={v}>
                {v}
              </option>)}
            </select>
            : <input
              required={Boolean(c['obrigatorio']) && c['tipo'] !== 'booleano'}
              type={c['tipo'] === 'numero'
              ? 'number'
              : c['tipo'] === 'data' ? 'date' : c['tipo'] === 'booleano' ? 'checkbox' : 'text'}
              checked={c['tipo'] === 'booleano' ? Boolean(extras[String(c['chave'])]) : undefined}
              value={c['tipo'] === 'booleano' ? undefined : String(extras[String(c['chave'])] ?? '')}
              onChange={e => setExtras(
                v => ({
                  ...v,
                  [String(c['chave'])]: c['tipo'] === 'numero'
                  ? Number(e.target.value)
                  : c['tipo'] === 'booleano' ? e.target.checked : e.target.value
                })
              )} />}
          </label>
        ))}

        {recurso === 'modelo' && <p>
          {t('modeloAjuda')}
        </p>}

        {recurso === 'aparencia'
        && <div
          className='configuracoes__marca'
          style={tokensDaMarca(
            Number(form['acento']),
            form['tema'] === 'claro' ? 'claro' : 'escuro',
          ) as CSSProperties}
        >
          {t('preview')}: {String(form['acento'])}
        </div>}

        <div className='configuracoes__acoes'>
          <button
            className='botao botao--primario'
            disabled={ocupado || recurso === 'empresa' && (camposExtras.isPending || camposExtras.isError)}
            type='submit'>
            {ocupado ? t('carregando') : t('salvar')}
          </button>
          {recurso !== 'aparencia'
          && <button className='botao' type='button' onClick={reset}>
            {t('cancelar')}
          </button>}
        </div>

        {erro
        && <div role='alert' className='configuracoes__erro'>
          <p>
            {erro}
          </p>
          {falhas.length > 0
          && <ul>
            {falhas.map(
              (campo, i) => <li key={i}>
                {campos[recurso].some(c => c.chave === campo)
                  ? t(campo as ChaveConfiguracoes)
                  : campo}: {t('valorInvalido')}
              </li>
            )}
          </ul>}
        </div>}
        {sucesso && <p role='status' className='configuracoes__sucesso'>
          {t('sucesso')}
        </p>}

      </form>

      <Historico recurso={recurso} {...(item ? {
        id: String(item['id'])
      } : {})} />

    </>}

  </div>;
}

function Empresa() {
  return <>
    <Editor recurso='empresa' />
    <Editor recurso='funcao' />
    <CatalogosExternos  />
  </>;
}

function Aparencia() {
  return <Editor recurso='aparencia' />;
}

function Sequencias() {
  return <Editor recurso='sequencia' />;
}

function Campos() {
  return <Editor recurso='campo' />;
}

function Visoes() {
  return <Editor recurso='visao' />;
}

function Modelos() {
  return <Editor recurso='modelo' />;
}

/** Meta de vendas do mes, que o Placar de vendas do Inicio usa. */
function MetasDeVendas() {
  return <Editor recurso='meta_vendas' />;
}

export const tela: TelaDeModulo = {
  slug: 'configuracoes',
  // rota propria: em /admin a tela de Admin vencia e esta nunca abria
  rota: '/configuracoes',
  get titulo() {
    return rotuloAtual('titulo');
  },
  abas: [

    {
      id: 'empresa',
      get rotulo() {
        return rotuloAtual('empresa');
      },
      conteudo: Empresa
    },

    {
      id: 'aparencia',
      get rotulo() {
        return rotuloAtual('aparencia');
      },
      conteudo: Aparencia
    },

    {
      id: 'sequencias',
      get rotulo() {
        return rotuloAtual('sequencia');
      },
      conteudo: Sequencias
    },

    {
      id: 'campos',
      get rotulo() {
        return rotuloAtual('campo');
      },
      conteudo: Campos
    },

    {
      id: 'visoes',
      get rotulo() {
        return rotuloAtual('visao');
      },
      conteudo: Visoes
    },

    {
      id: 'modelos',
      get rotulo() {
        return rotuloAtual('modelo');
      },
      conteudo: Modelos
    },

    {
      id: 'metas',
      get rotulo() {
        return rotuloAtual('meta_vendas');
      },
      conteudo: MetasDeVendas
    },

  ]
};
